import React, { useState, useEffect, useRef } from 'react';
import '../styles/profile.css';

/* ---------------------------------------------------------------- *
 *  Helpers
 * ---------------------------------------------------------------- */

// Deterministic accent color for the avatar ring, derived from the name.
const AVATAR_PALETTE = [
  '#3D5AFE',
  '#0EA5A4',
  '#E8590C',
  '#7C3AED',
  '#D6336C',
  '#2F9E44'
];

const getAvatarColor = (seed) => {
  if (!seed) return AVATAR_PALETTE[0];

  let hash = 0;

  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }

  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
};

const getInitials = (name) => {
  if (!name || !name.trim()) return '?';

  const parts = name.trim().split(/\s+/);

  const first = parts[0]?.[0] || '';
  const last = parts.length > 1
    ? parts[parts.length - 1][0]
    : '';

  return (first + last).toUpperCase();
};

const clamp = (value, min, max) =>
  Math.min(Math.max(value, min), max);

const MAX_FILE_MB = 10;
const MAX_ZOOM = 4;
const OUTPUT_SIZE = 512;
const CROP_RATIO = 0.8;


/* ---------------------------------------------------------------- *
 *  PhotoCropper
 *  Drag to move, pinch / scroll / slider to zoom.
 *  Returns a square JPEG (512 x 512).
 * ---------------------------------------------------------------- */

const PhotoCropper = ({ file, onCancel, onApply }) => {
  const [src, setSrc] = useState('');
  const [nat, setNat] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [center, setCenter] = useState({ x: 0, y: 0 });
  const [stageW, setStageW] = useState(320);
  const [dragging, setDragging] = useState(false);

  const stageRef = useRef(null);
  const imgRef = useRef(null);
  const pointers = useRef(new Map());
  const pinch = useRef({
    startDist: 0,
    startZoom: 1
  });

  // Object URL for chosen file
  useEffect(() => {
    const url = URL.createObjectURL(file);

    setSrc(url);

    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Responsive stage width
  useEffect(() => {
    const el = stageRef.current;

    if (!el) return undefined;

    const update = () => setStageW(el.clientWidth);

    update();

    const observer = new ResizeObserver(update);

    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  // Escape closes cropper + lock background scroll
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };

    document.addEventListener('keydown', onKey);

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);

      document.body.style.overflow = previousOverflow;
    };
  }, [onCancel]);

  const minSide = nat ? Math.min(nat.w, nat.h) : 1;

  const cropBox = stageW * CROP_RATIO;

  const scale = (cropBox * zoom) / minSide;

  const clampCenter = (c, z) => {
    if (!nat) return c;

    const half = minSide / z / 2;

    return {
      x: clamp(c.x, half, nat.w - half),
      y: clamp(c.y, half, nat.h - half)
    };
  };

  const changeZoom = (value) => {
    if (!nat) return;

    const z = clamp(value, 1, MAX_ZOOM);

    setZoom(z);

    setCenter((c) => clampCenter(c, z));
  };

  const handleImageLoad = (e) => {
    const {
      naturalWidth: w,
      naturalHeight: h
    } = e.target;

    setNat({ w, h });

    setCenter({
      x: w / 2,
      y: h / 2
    });

    setZoom(1);
  };

  const reset = () => {
    if (!nat) return;

    setZoom(1);

    setCenter({
      x: nat.w / 2,
      y: nat.h / 2
    });
  };

  /* ---- pointer gestures ---- */

  const handlePointerDown = (e) => {
    if (!nat) return;

    e.currentTarget.setPointerCapture(e.pointerId);

    pointers.current.set(e.pointerId, {
      x: e.clientX,
      y: e.clientY
    });

    setDragging(true);

    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];

      pinch.current = {
        startDist: Math.hypot(
          a.x - b.x,
          a.y - b.y
        ),
        startZoom: zoom
      };
    }
  };

  const handlePointerMove = (e) => {
    const prev = pointers.current.get(e.pointerId);

    if (!prev || !nat) return;

    pointers.current.set(e.pointerId, {
      x: e.clientX,
      y: e.clientY
    });

    if (pointers.current.size === 1) {
      const dx = e.clientX - prev.x;
      const dy = e.clientY - prev.y;

      setCenter((c) =>
        clampCenter(
          {
            x: c.x - dx / scale,
            y: c.y - dy / scale
          },
          zoom
        )
      );
    } else if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];

      const dist = Math.hypot(
        a.x - b.x,
        a.y - b.y
      );

      if (pinch.current.startDist > 0) {
        changeZoom(
          pinch.current.startZoom *
          (dist / pinch.current.startDist)
        );
      }
    }
  };

  const handlePointerUp = (e) => {
    pointers.current.delete(e.pointerId);

    if (pointers.current.size === 0) {
      setDragging(false);
    }
  };

  const handleWheel = (e) => {
    changeZoom(
      zoom * (1 - e.deltaY * 0.002)
    );
  };

  const handleKeyDown = (e) => {
    if (!nat) return;

    const step = 20 / scale;

    const moves = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step]
    };

    if (moves[e.key]) {
      e.preventDefault();

      const [dx, dy] = moves[e.key];

      setCenter((c) =>
        clampCenter(
          {
            x: c.x + dx,
            y: c.y + dy
          },
          zoom
        )
      );
    } else if (e.key === '+' || e.key === '=') {
      changeZoom(zoom + 0.1);
    } else if (e.key === '-') {
      changeZoom(zoom - 0.1);
    }
  };

  /* ---- export crop ---- */

  const applyCrop = () => {
    if (!nat || !imgRef.current) return;

    const side = minSide / zoom;

    const canvas = document.createElement('canvas');

    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;

    const ctx = canvas.getContext('2d');

    ctx.fillStyle = '#ffffff';

    ctx.fillRect(
      0,
      0,
      OUTPUT_SIZE,
      OUTPUT_SIZE
    );

    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      imgRef.current,
      center.x - side / 2,
      center.y - side / 2,
      side,
      side,
      0,
      0,
      OUTPUT_SIZE,
      OUTPUT_SIZE
    );

    const dataUrl = canvas.toDataURL(
      'image/jpeg',
      0.9
    );

    canvas.toBlob(
      (blob) => {
        if (blob) {
          onApply({
            blob,
            dataUrl
          });
        }
      },
      'image/jpeg',
      0.9
    );
  };

  const translateX =
    stageW / 2 - center.x * scale;

  const translateY =
    stageW / 2 - center.y * scale;

  return (
    <div
      className="crop-overlay"
      onMouseDown={(e) =>
        e.target === e.currentTarget && onCancel()
      }
    >
      <div
        className="crop-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="crop-title"
      >
        <header className="crop-header">
          <div>
            <h2 id="crop-title">
              Adjust your photo
            </h2>

            <p>
              Drag to move. Zoom until your face fits the frame.
            </p>
          </div>

          <button
            type="button"
            className="icon-btn1"
            onClick={onCancel}
            aria-label="Close"
          >
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              aria-hidden="true"
            >
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                fill="none"
              />
            </svg>
          </button>
        </header>

        <div
          ref={stageRef}
          className={`crop-stage ${
            dragging ? 'is-dragging' : ''
          }`}
          tabIndex={0}
          role="application"
          aria-label="Photo editor. Use arrow keys to move and plus or minus to zoom."
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onWheel={handleWheel}
          onKeyDown={handleKeyDown}
        >
          {src && (
            <img
              ref={imgRef}
              src={src}
              alt=""
              draggable={false}
              onLoad={handleImageLoad}
              className="crop-image"
              style={
                nat
                  ? {
                      width: nat.w * scale,
                      height: nat.h * scale,
                      transform: `translate3d(${translateX}px, ${translateY}px, 0)`
                    }
                  : {
                      opacity: 0
                    }
              }
            />
          )}

          <div
            className="crop-frame"
            aria-hidden="true"
          />

          {!nat && (
            <div className="crop-loading">
              Loading photo…
            </div>
          )}
        </div>

        <p className="crop-note">
          The dashed circle shows how your photo appears on your profile.
        </p>

        <div className="zoom-row">
          <button
            type="button"
            className="icon-btn1"
            onClick={() =>
              changeZoom(zoom - 0.25)
            }
            aria-label="Zoom out"
          >
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              aria-hidden="true"
            >
              <path
                d="M5 12h14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                fill="none"
              />
            </svg>
          </button>

          <input
            type="range"
            min="1"
            max={MAX_ZOOM}
            step="0.01"
            value={zoom}
            onChange={(e) =>
              changeZoom(
                parseFloat(e.target.value)
              )
            }
            aria-label="Zoom"
            className="zoom-slider"
          />

          <button
            type="button"
            className="icon-btn1"
            onClick={() =>
              changeZoom(zoom + 0.25)
            }
            aria-label="Zoom in"
          >
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              aria-hidden="true"
            >
              <path
                d="M12 5v14M5 12h14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                fill="none"
              />
            </svg>
          </button>
        </div>

        <footer className="crop-footer">
          <button
            type="button"
            className="btn1-ghost"
            onClick={reset}
          >
            Reset
          </button>

          <div className="crop-actions">
            <button
              type="button"
              className="btn1-secondary"
              onClick={onCancel}
            >
              Cancel
            </button>

            <button
              type="button"
              className="btn1-primary"
              onClick={applyCrop}
              disabled={!nat}
              autoFocus
            >
              Use photo
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};


/* ---------------------------------------------------------------- *
 *  ProfilePage
 * ---------------------------------------------------------------- */

const ProfilePage = () => {
  // Empty fields for editing
  const [formData, setFormData] = useState({
    user_profile: '',
    user_bio: '',
    organization: '',
    designation: ''
  });

  // Saved profile shown in preview
  const [savedProfile, setSavedProfile] = useState({
    user_profile: '',
    user_bio: '',
    organization: '',
    designation: ''
  });

  const [selectedFile, setSelectedFile] = useState(null);

  const [sourceFile, setSourceFile] = useState(null);

  const [cropFile, setCropFile] = useState(null);

  const [fileName, setFileName] = useState('');

  const [loading, setLoading] = useState(false);

  const [feedback, setFeedback] = useState({
    show: false,
    message: '',
    isError: false
  });

  const [profileImage, setProfileImage] =
    useState(null);


  /* -------------------------------------------------------------- *
   * Restore saved profile from localStorage
   * -------------------------------------------------------------- */

  useEffect(() => {
    try {
      const saved = JSON.parse(
        localStorage.getItem('userProfile')
      );

      if (saved) {
        setSavedProfile({
          user_profile: saved.user_profile || '',
          user_bio: saved.user_bio || '',
          organization: saved.organization || '',
          designation: saved.designation || ''
        });

        // Restore saved profile image
        setProfileImage(
          saved.profile_image || null
        );

        // Keep form fields EMPTY
        setFormData({
          user_profile: '',
          user_bio: '',
          organization: '',
          designation: ''
        });
      }
    } catch (error) {
      console.error(
        'Error reading saved profile:',
        error
      );
    }
  }, []);


  /* -------------------------------------------------------------- *
   * Form input
   * -------------------------------------------------------------- */

  const handleInputChange = (e) => {
    const {
      name,
      value
    } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };


  /* -------------------------------------------------------------- *
   * Feedback
   * -------------------------------------------------------------- */

  const showFeedback = (
    message,
    isError = false
  ) => {
    setFeedback({
      show: true,
      message,
      isError
    });

    setTimeout(() => {
      setFeedback({
        show: false,
        message: '',
        isError: false
      });
    }, 4000);
  };


  /* -------------------------------------------------------------- *
   * Photo selection
   * -------------------------------------------------------------- */

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    // Allows selecting same file again
    e.target.value = '';

    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showFeedback(
        'Choose an image file (PNG, JPG, GIF or WebP).',
        true
      );

      return;
    }

    if (
      file.size >
      MAX_FILE_MB * 1024 * 1024
    ) {
      showFeedback(
        `Photo is too large. Choose one under ${MAX_FILE_MB} MB.`,
        true
      );

      return;
    }

    setSourceFile(file);

    setCropFile(file);
  };


  /* -------------------------------------------------------------- *
   * Crop apply
   * -------------------------------------------------------------- */

  const handleCropApply = ({
    blob,
    dataUrl
  }) => {
    const baseName =
      cropFile.name.replace(
        /\.[^.]+$/,
        ''
      );

    const cropped = new File(
      [blob],
      `${baseName}-cropped.jpg`,
      {
        type: 'image/jpeg'
      }
    );

    setSelectedFile(cropped);

    setFileName(cropped.name);

    setProfileImage(dataUrl);

    setCropFile(null);
  };


  /* -------------------------------------------------------------- *
   * Remove photo
   * -------------------------------------------------------------- */

  const handleRemovePhoto = () => {
    setSelectedFile(null);

    setSourceFile(null);

    setFileName('');

    setProfileImage(null);
  };


  /* -------------------------------------------------------------- *
   * Validation
   * -------------------------------------------------------------- */

  const validateForm = () => {
    const hasTextChange =
      formData.user_profile.trim() ||
      formData.user_bio.trim() ||
      formData.organization.trim() ||
      formData.designation.trim();

    const hasPhotoChange =
      selectedFile;

    if (!hasTextChange && !hasPhotoChange) {
      showFeedback(
        'Please enter something to update.',
        true
      );

      return false;
    }

    return true;
  };


  /* -------------------------------------------------------------- *
   * Save profile - LOCAL STORAGE ONLY
   * -------------------------------------------------------------- */

  const submitProfile = async () => {
    if (!validateForm()) return;

    setLoading(true);

    try {
      /*
       * Merge new values with existing saved profile.
       *
       * If a field is empty:
       * keep the existing saved value.
       */
      const updatedProfile = {
        user_profile:
          formData.user_profile.trim() ||
          savedProfile.user_profile,

        user_bio:
          formData.user_bio.trim() ||
          savedProfile.user_bio,

        organization:
          formData.organization.trim() ||
          savedProfile.organization,

        designation:
          formData.designation.trim() ||
          savedProfile.designation,

        profile_image:
          profileImage,

        updatedAt:
          new Date().toISOString()
      };


      /* ---------------------------------------------------------- *
       * Save to localStorage
       * ---------------------------------------------------------- */

      localStorage.setItem(
        'userProfile',
        JSON.stringify(updatedProfile)
      );


      /* ---------------------------------------------------------- *
       * Update preview immediately
       * ---------------------------------------------------------- */

      setSavedProfile({
        user_profile:
          updatedProfile.user_profile,

        user_bio:
          updatedProfile.user_bio,

        organization:
          updatedProfile.organization,

        designation:
          updatedProfile.designation
      });


      /* ---------------------------------------------------------- *
       * Tell navbar / other components
       * ---------------------------------------------------------- */

      window.dispatchEvent(
        new CustomEvent(
          'profileSubmitted',
          {
            detail: {
              profileData:
                updatedProfile
            }
          }
        )
      );


      /* ---------------------------------------------------------- *
       * Clear form fields after save
       * ---------------------------------------------------------- */

      setFormData({
        user_profile: '',
        user_bio: '',
        organization: '',
        designation: ''
      });

      setSelectedFile(null);

      setFileName('');


      showFeedback(
        'Profile saved.',
        false
      );

    } catch (error) {
      console.error(
        'Error saving profile:',
        error
      );

      showFeedback(
        'Could not save your profile.',
        true
      );

    } finally {
      setLoading(false);
    }
  };


  /* -------------------------------------------------------------- *
   * Avatar
   * -------------------------------------------------------------- */

  const avatarColor =
    getAvatarColor(
      savedProfile.user_profile
    );


  /* -------------------------------------------------------------- *
   * UI
   * -------------------------------------------------------------- */

  return (
    <div className="profile-container">

      <div className="page-header">

        <span className="page-eyebrow">
          Account
        </span>

        <h1>
          Your profile
        </h1>

        <p>
          This is how you'll appear across the platform.
        </p>

      </div>


      <div className="profile-grid">

        {/* Preview panel */}

        <aside className="preview-card">

          <div
            className="avatar-frame"
            style={{
              '--avatar-color':
                avatarColor
            }}
          >

            {profileImage ? (

              <img
                src={profileImage}
                alt="Profile preview"
                className="avatar-image"
              />

            ) : (

              <span className="avatar-initials">
                {getInitials(
                  savedProfile.user_profile
                )}
              </span>

            )}

          </div>


          <h3 className="preview-name">

            {savedProfile.user_profile.trim() ||
              'Your name'}

          </h3>


          {(savedProfile.designation ||
            savedProfile.organization) && (

            <p className="preview-role">

              {savedProfile.designation}

              {savedProfile.designation &&
                savedProfile.organization
                ? ' · '
                : ''}

              {savedProfile.organization}

            </p>

          )}


          {savedProfile.user_bio.trim() && (

            <p className="preview-bio">
              {savedProfile.user_bio}
            </p>

          )}


          <input
            type="file"
            id="filestoupload"
            onChange={handleFileChange}
            accept="image/*"
            className="visually-hidden-input"
          />


          <label
            htmlFor="filestoupload"
            className="preview-upload"
          >
            {profileImage
              ? 'Change photo'
              : 'Upload photo'}
          </label>


          {profileImage && (

            <div className="photo-actions">

              {sourceFile && (

                <button
                  type="button"
                  className="btn1-link"
                  onClick={() =>
                    setCropFile(sourceFile)
                  }
                >
                  Adjust
                </button>

              )}


              <button
                type="button"
                className="btn1-link btn1-link-danger"
                onClick={handleRemovePhoto}
              >
                Remove
              </button>

            </div>

          )}


          {fileName && (

            <span className="preview-filename">
              {fileName}
            </span>

          )}

        </aside>


        {/* Edit form */}

        <div className="form-card">

          <div className="field-row">

            <label htmlFor="user_profile">
              Display name
              <span className="required">
                *
              </span>
            </label>

            <input
              id="user_profile"
              type="text"
              name="user_profile"
              value={formData.user_profile}
              onChange={handleInputChange}
              placeholder="e.g. Alex Morgan"
              autoComplete="name"
              required
            />

          </div>


          <div className="field-row">

            <label htmlFor="user_bio">
              Bio
            </label>

            <textarea
              id="user_bio"
              name="user_bio"
              value={formData.user_bio}
              onChange={handleInputChange}
              placeholder="Tell people a little about yourself..."
              rows="4"
            />

          </div>


          <div className="field-split">

            <div className="field-row">

              <label htmlFor="organization">
                Organization
              </label>

              <input
                id="organization"
                type="text"
                name="organization"
                value={formData.organization}
                onChange={handleInputChange}
                placeholder="Company / Org"
                autoComplete="organization"
              />

            </div>


            <div className="field-row">

              <label htmlFor="designation">
                Designation
              </label>

              <input
                id="designation"
                type="text"
                name="designation"
                value={formData.designation}
                onChange={handleInputChange}
                placeholder="Role"
                autoComplete="organization-title"
              />

            </div>

          </div>


          <div className="form-footer">

            {feedback.show && (

              <div
                className={`status-message ${
                  feedback.isError
                    ? 'error-message'
                    : ''
                }`}
                role={
                  feedback.isError
                    ? 'alert'
                    : 'status'
                }
              >
                {feedback.message}
              </div>

            )}


            <button
              type="button"
              className="btn1-primary"
              onClick={submitProfile}
              disabled={loading}
            >

              {loading
                ? 'Saving...'
                : 'Save profile'}

            </button>

          </div>

        </div>

      </div>


      {cropFile && (

        <PhotoCropper
          file={cropFile}
          onCancel={() =>
            setCropFile(null)
          }
          onApply={handleCropApply}
        />

      )}

    </div>
  );
};

export default ProfilePage;